import {
  LighthouseRestApi,
  LighthouseApi,
  TriggerAuditPayload,
  Audit,
  Website,
  WebsiteListResponse,
  LASListRequest,
} from '@backstage-community/plugin-lighthouse-common';
import { IdentityApi } from '@backstage/core-plugin-api';
import { LighthouseTagsApi } from './lighthouseTagsApi';

// Lighthouse's desktop preset; without it the audit service keeps mobile Slow 4G throttling.
export const DESKTOP_LIGHTHOUSE_SETTINGS = {
  formFactor: 'desktop' as const,
  emulatedFormFactor: 'desktop' as const,
  screenEmulation: {
    mobile: false,
    width: 1350,
    height: 940,
    deviceScaleFactor: 1,
    disabled: false,
  },
  throttling: {
    rttMs: 40,
    throughputKbps: 10 * 1024,
    cpuSlowdownMultiplier: 1,
    requestLatencyMs: 0,
    downloadThroughputKbps: 0,
    uploadThroughputKbps: 0,
  },
};

/**
 * Wraps the standard LighthouseRestApi to automatically tag audits
 * with the current user's full Azure AD display name on triggerAudit().
 */
export class LighthouseApiWithTags implements LighthouseApi {
  private readonly inner: LighthouseRestApi;
  private readonly tagsApi: LighthouseTagsApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    inner: LighthouseRestApi;
    tagsApi: LighthouseTagsApi;
    identityApi: IdentityApi;
  }) {
    this.inner = options.inner;
    this.tagsApi = options.tagsApi;
    this.identityApi = options.identityApi;
  }

  get url(): string {
    return this.inner.url;
  }

  async getWebsiteList(options?: LASListRequest): Promise<WebsiteListResponse> {
    return this.inner.getWebsiteList(options);
  }

  async getWebsiteForAuditId(auditId: string): Promise<Website> {
    return this.inner.getWebsiteForAuditId(auditId);
  }

  async getWebsiteByUrl(websiteUrl: string): Promise<Website> {
    return this.inner.getWebsiteByUrl(websiteUrl);
  }

  async triggerAudit(payload: TriggerAuditPayload): Promise<Audit> {
    const settings = payload.options.lighthouseConfig.settings;
    const auditPayload: TriggerAuditPayload =
      settings.formFactor === 'desktop'
        ? {
            ...payload,
            options: {
              ...payload.options,
              lighthouseConfig: {
                ...payload.options.lighthouseConfig,
                settings: { ...settings, ...DESKTOP_LIGHTHOUSE_SETTINGS },
              },
            },
          }
        : payload;
    const audit = await this.inner.triggerAudit(auditPayload);

    // Auto-tag with the user's full Azure AD display name
    try {
      const profile = await this.identityApi.getProfileInfo();
      const displayName = profile.displayName || 'Unknown';

      await this.tagsApi.setTag(audit.id, payload.url, displayName);
    } catch (_err) {
      // Don't fail the audit if tagging fails – swallowed intentionally
    }

    return audit;
  }
}
